package org.matthewo.pages.event.tabs

import emotion.react.css
import js.objects.buildReadonlyRecord
import js.objects.unsafeJso
import org.matthewo.YEAR_PARAM
import org.matthewo.api.AdvancementType
import org.matthewo.api.EventCompetitionSimple
import org.matthewo.js.toFixed
import org.matthewo.pages.event.EventTabFC
import org.matthewo.pages.team.TEAM_PARAM
import org.matthewo.pages.team.tabs.EVENT_CODE_SEARCH
import org.matthewo.pages.team.tabs.TEAM_PATH
import org.matthewo.pages.team.tabs.toTeam
import org.matthewo.recharts.cartesian.Bar
import org.matthewo.recharts.cartesian.CartesianGrid
import org.matthewo.recharts.cartesian.XAxis
import org.matthewo.recharts.cartesian.YAxis
import org.matthewo.recharts.chart.BarChart
import org.matthewo.recharts.component.Cell
import org.matthewo.recharts.component.Label
import org.matthewo.recharts.component.Tooltip
import org.matthewo.recharts.container.Legend
import org.matthewo.recharts.container.ResponsiveContainer
import org.matthewo.recharts.type.AxisType
import org.matthewo.recharts.type.ChartMargin
import org.matthewo.recharts.type.ComponentPosition
import org.matthewo.recharts.type.LayoutDirection
import org.matthewo.router.contains
import org.matthewo.routes.API
import org.matthewo.styles.*
import react.FC
import react.dom.events.PointerType
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.p
import react.useMemo
import react.useState
import tanstack.react.router.useNavigate
import tanstack.react.router.useSearch
import web.cssom.*
import web.dom.document
import web.window.window

val EventBarChart = EventTabFC<API.Event.Stats, List<EventCompetitionSimple>>({ event, _, searchParams ->
    API.Event.Stats(event, "pre" in searchParams, "advancement" in searchParams)
}) { (coroutineScope, year, eventCode, data) ->
    val searchParams = useSearch()
    val navigate = useNavigate()

    var touch by useState(false)

    val total = useMemo(searchParams) {
        "total" in searchParams
    }

    val teams = data?.toTypedArray() ?: []

    val EventBarTooltip = FC("EventBarTooltip") { propsTooltip: dynamic ->
        if (!propsTooltip["active"].unsafeCast<Boolean>()) return@FC
        val payload = propsTooltip["payload"][0]
        if (undefined == payload) return@FC
        val instance = payload["payload"].unsafeCast<EventCompetitionSimple>()
        div {
            css {
                opacity = number(0.9)
                background = BACKGROUND_COLOR
                border = Border(2.px, LineStyle.solid, TITLE_FONT_COLOR)
                borderRadius = 4.px
                color = NamedColor.white
                padding = array(0.0.em, 0.5.em)

                if (touch) pointerEvents = Auto.auto

                ".label" {
                    fontWeight = FontWeight.bold
                }
            }
            onPointerDown = { event ->
                if (touch) navigate {
                    toTeam(year, instance.number, eventCode)
                }
            }
            p {
                className = ClassName("label")
                b {
                    css {
                        textDecoration = TextDecoration.underline
                    }
                    +"${instance.number} - ${instance.name}"
                }
            }
            p {
                className = ClassName("intro")
                +"NP Total: ${instance.npOpr.toFixed(1)}"
            }
            p {
                className = ClassName("intro")
                +"Auto: ${instance.autoOpr.toFixed(1)}"
            }
            p {
                className = ClassName("intro")
                +"Teleop: ${instance.teleopOpr.toFixed(1)}"
            }
            p {
                className = ClassName("intro")
                +"Endgame: ${instance.endgameOpr.toFixed(1)}"
            }
        }
    }

    div {
        css {
            width = 100.pct
            height = 100.pct
            fontSize = 1.rem
            overflow = Overflow.hidden

            ".recharts-rectangle.recharts-tooltip-cursor" {
                this[Selector("fill")] = "#00000049"
            }
        }
        onPointerDown = { event -> touch = PointerType.mouse != event.pointerType }
        ResponsiveContainer {
            width = 100.pct
            height = 100.pct
            BarChart {
                onClick = { event ->
                    if (!touch) {
                        val payload = event["activePayload"]
                        if (undefined != payload) {
                            val competition = payload[0]["payload"].unsafeCast<EventCompetitionSimple>()
                            navigate {
                                to = TEAM_PATH
                                params = buildReadonlyRecord {
                                    this[YEAR_PARAM] = year.toString()
                                    this[TEAM_PARAM] = competition.number.toString()
                                }
                                search = buildReadonlyRecord {
                                    this[EVENT_CODE_SEARCH] = eventCode
                                }
                            }
                        }
                    }
                }

                margin = ChartMargin(20, 25, 10, 15)

                this.data = teams
                CartesianGrid {
                    strokeDasharray = "6 3"
                    vertical = false
                    horizontal = data?.isNotEmpty() ?: false
                }
                val charWidth = 0.7 * getComputedStyle(document.documentElement).fontSize.dropLast(2).toDouble()
                if (window.innerWidth / teams.size.toDouble() > charWidth) {
                    XAxis {
                        dataKey = { instance: EventCompetitionSimple -> instance.number }
                        type = AxisType.category
                        interval = 0
                        angle = -45
                        height = 55
                        tick = unsafeJso {
                            this["dx"] = -15
                            this["dy"] = 15
                        }
                    }
                }
                YAxis {
                    Label {
                        value = "OPRN"
                        position = ComponentPosition.insideLeft
                        angle = -90
                        style = unsafeJso {
                            textAnchor = "middle"
                        }
                    }
                }
                Legend {
                    layout = LayoutDirection.horizontal
                    onClick = {
                        navigate {
                            search = buildReadonlyRecord {
                                if (!total) this["total"] = Unit
                            }
                        }
                    }
                }

                val bars =
                    if (total)
                        [
                            Triple({ e: EventCompetitionSimple -> e.autoOpr + e.teleopOpr + e.endgameOpr }, TOTAL_COLOR, "NP Total"),
                        ]
                    else
                        [
                            Triple(EventCompetitionSimple::autoOpr, AUTO_COLOR, "Auto"),
                            Triple(EventCompetitionSimple::teleopOpr, TELEOP_COLOR, "Teleop"),
                            Triple(EventCompetitionSimple::endgameOpr, ENDGAME_COLOR, "Endgame"),
                        ]

                bars.forEach { (metric, color, title) ->
                    Bar {
                        dataKey = { instance: EventCompetitionSimple ->
                            metric(instance)
                        }
                        stroke = color.chart.toString()
                        fill = color.chart.toString()
                        strokeWidth = 2
                        name = title
                        if (total) {
                            this@BarChart.data.forEach { competition: EventCompetitionSimple ->
                                Cell {
                                    val color = when (competition.advancement) {
                                        AdvancementType.ELIM -> AUTO_COLOR
                                        AdvancementType.AWARD -> ENDGAME_COLOR
                                        else -> TOTAL_COLOR
                                    }
                                    stroke = color.chart.toString()
                                    fill = color.chart.toString()
                                }
                            }
                        }
                    }
                }
                Tooltip {
                    content = EventBarTooltip
                }
            }
        }
    }
}
